import React from "react";
import { Button } from "@/components/ui/button";
import { Check, RotateCcw, Sparkles } from "lucide-react";

const OPTIONS = ["A", "B", "C", "D", "E"];

/**
 * OpticalSheet Component
 * 5-option interactive optical form (A, B, C, D, E)
 *
 * @param {number} questionCount - Total number of questions
 * @param {string} value - String of answers, e.g. "ABCDE A B C"
 * @param {function} onChange - Callback when answers change
 * @param {string} answerKey - Optional answer key for comparison mode
 * @param {array} kazanimlar - Optional list of learning outcomes
 * @param {boolean} readOnly - If true, bubbles are not clickable
 * @param {string} title - Optional title for this sheet section
 */
export default function OpticalSheet({
  questionCount = 40,
  value = "",
  onChange,
  answerKey = "",
  kazanimlar = [],
  readOnly = false,
  title = "",
}) {
  const qc = Math.max(1, Number(questionCount) || 1);
  const rawAnswers = (value || "").toUpperCase();
  const rawKey = (answerKey || "").toUpperCase();

  // Convert string to array of length qc
  const marks = Array.from({ length: qc }).map((_, i) => rawAnswers[i] || " ");

  const handleBubbleClick = (qIndex, opt) => {
    if (readOnly || !onChange) return;
    const current = marks[qIndex] || " ";
    const newChar = current === opt ? " " : opt; // click selected again to clear

    // Build new string
    const newMarks = [...marks];
    newMarks[qIndex] = newChar;

    // Trim trailing spaces or keep length
    const updatedStr = newMarks.join("");
    onChange(updatedStr);
  };

  const handleClearAll = () => {
    if (readOnly || !onChange) return;
    onChange("");
  };

  const handleFillAll = (letter) => {
    if (readOnly || !onChange) return;
    onChange(letter.repeat(qc));
  };

  // Group questions into columns of 10 or 20
  const perCol = qc <= 20 ? 10 : 20;
  const numCols = Math.ceil(qc / perCol);

  // Statistics
  const answeredCount = marks.filter((m) => m && m !== " ").length;
  const blankCount = qc - answeredCount;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3" data-testid="optical-sheet">
      {/* Header & Controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
        <div className="flex items-center gap-2">
          {title && <span className="font-display font-bold text-slate-800 text-sm">{title}</span>}
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {qc} Soru
          </span>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
            {answeredCount} İşaretli · {blankCount} Boş
          </span>
        </div>

        {!readOnly && onChange && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClearAll}
              className="h-7 text-xs border-slate-200 text-slate-600 hover:text-rose-600 px-2"
              data-testid="optical-clear-btn"
            >
              <RotateCcw size={11} className="mr-1" /> Temizle
            </Button>
            <div className="hidden sm:flex items-center gap-1 text-[11px] text-slate-400 border-l border-slate-200 pl-2">
              <span>Hızlı:</span>
              {OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleFillAll(opt)}
                  className="w-5 h-5 rounded text-[10px] font-bold bg-slate-100 hover:bg-indigo-100 hover:text-indigo-700 text-slate-600"
                  title={`Tümünü ${opt} yap`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Optical Bubble Grid (Columns of questions) */}
      <div
        className="grid gap-4 overflow-x-auto py-1"
        style={{
          gridTemplateColumns: `repeat(${numCols}, minmax(190px, 1fr))`,
        }}
        data-testid="optical-grid"
      >
        {Array.from({ length: numCols }).map((_, colIdx) => {
          const startQ = colIdx * perCol;
          const endQ = Math.min(qc, (colIdx + 1) * perCol);
          const colQuestions = Array.from({ length: endQ - startQ }).map((_, i) => startQ + i);

          return (
            <div
              key={colIdx}
              className="space-y-1.5 bg-slate-50/70 p-2.5 rounded-xl border border-slate-200/80"
            >
              <div className="grid grid-cols-[28px_repeat(5,1fr)] gap-1 text-[10px] font-extrabold text-slate-400 text-center pb-1 border-b border-slate-200">
                <span>No</span>
                {OPTIONS.map((opt) => (
                  <span key={opt}>{opt}</span>
                ))}
              </div>

              {colQuestions.map((qIdx) => {
                const qNum = qIdx + 1;
                const userMark = marks[qIdx] || " ";
                const correctMark = rawKey[qIdx] || "";
                const kaz = kazanimlar[qIdx] || "";

                return (
                  <div
                    key={qIdx}
                    className="grid grid-cols-[28px_repeat(5,1fr)] gap-1 items-center"
                    data-testid={`optical-q-${qNum}`}
                    title={kaz ? `Soru ${qNum}: ${kaz}` : `Soru ${qNum}`}
                  >
                    {/* Question Number */}
                    <span className="text-[11px] font-extrabold text-slate-600 text-center font-mono">
                      {qNum}
                    </span>

                    {/* 5 Bubbles: A, B, C, D, E */}
                    {OPTIONS.map((opt) => {
                      const isSelected = userMark === opt;
                      const isCorrectAnswer = correctMark === opt;
                      const hasComparison = !!rawKey && readOnly;

                      // Determine bubble style
                      let bubbleStyle =
                        "w-6 h-6 sm:w-7 sm:h-7 mx-auto rounded-full text-xs font-bold transition-all flex items-center justify-center ";

                      if (hasComparison) {
                        // Review / Analysis Mode
                        if (isSelected && isCorrectAnswer) {
                          // Correct answer marked
                          bubbleStyle += "bg-emerald-600 text-white shadow-sm ring-2 ring-emerald-300";
                        } else if (isSelected && !isCorrectAnswer) {
                          // Wrong answer marked
                          bubbleStyle += "bg-rose-600 text-white shadow-sm ring-2 ring-rose-300";
                        } else if (!isSelected && isCorrectAnswer) {
                          // Correct answer that was not marked
                          bubbleStyle += "border-2 border-emerald-500 bg-emerald-50 text-emerald-800 font-extrabold";
                        } else {
                          // Normal unselected bubble
                          bubbleStyle += "border border-slate-200 text-slate-400 bg-white opacity-60";
                        }
                      } else {
                        // Standard interactive marking mode
                        if (isSelected) {
                          bubbleStyle +=
                            "bg-indigo-900 text-white border border-indigo-900 shadow-md scale-105";
                        } else {
                          bubbleStyle +=
                            "bg-white border border-slate-300 text-slate-700 hover:bg-indigo-50 hover:border-indigo-400 hover:text-indigo-900 shadow-sm " +
                            (readOnly ? "cursor-default opacity-60" : "cursor-pointer");
                        }
                      }

                      return (
                        <button
                          key={opt}
                          type="button"
                          disabled={readOnly}
                          onClick={() => handleBubbleClick(qIdx, opt)}
                          className={bubbleStyle}
                          data-testid={`optical-bubble-${qNum}-${opt}`}
                          title={
                            hasComparison
                              ? isSelected && isCorrectAnswer
                                ? "Doğru İşaretlendi"
                                : isSelected
                                ? `Yanlış (Doğru: ${correctMark})`
                                : isCorrectAnswer
                                ? "Doğru Cevap"
                                : opt
                              : `${qNum}. Soru - ${opt}`
                          }
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
